/**
 * Complete — finished matches that something was invested in.
 *
 * On the desktop this tab was an empty placeholder for most of its life, and the plan for this
 * migration says to keep it a placeholder. It is not one any more, and that is a deliberate
 * departure worth naming: the data to fill it is now sitting in D1 (a match with a prediction or a
 * bet is kept forever, which is the retention rule), and a screen that says "nothing here yet"
 * while holding 168 predictions is a worse lie than an empty tab.
 *
 * The filter is the same one the retention rule uses: a finished match is worth showing when a
 * prediction was paid for or a bet was placed on it. One that merely aged out with neither is noise.
 */
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { hasPrediction, isComplete, kickoffDisplay, kickoffMs, pct } from '@/models';
import { Empty } from '@/screens/Upcoming';
import { useStore } from '@/store';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

export function CompleteScreen() {
  const { matches, bets } = useStore();

  const betEventIds = React.useMemo(() => new Set(bets.map((b) => b.eventId)), [bets]);

  const shown = React.useMemo(
    () =>
      matches
        .filter((m) => isComplete(m) && (hasPrediction(m) || betEventIds.has(m.eventId)))
        .sort((a, b) => kickoffMs(b) - kickoffMs(a)),
    [matches, betEventIds],
  );

  return (
    <ScrollView contentContainerClassName="gap-3 pb-6">
      <Text variant="small" className="text-muted-foreground">
        {shown.length} finished, with a prediction or a bet on them
      </Text>

      {shown.length === 0 ? (
        <Empty
          title="Nothing finished yet"
          body="A match lands here once it is over and something was invested in it — a prediction that was paid for, or a bet that was placed."
        />
      ) : (
        shown.map((m) => {
          const placed = bets.filter((b) => b.eventId === m.eventId);
          return (
            <Card key={m.eventId}>
              <CardContent className="gap-1 py-3">
                <View className="flex-row items-start justify-between gap-3">
                  <View className="min-w-0 flex-1">
                    <Text variant="heading" numberOfLines={1}>
                      {m.homeTeam} v {m.awayTeam}
                    </Text>
                    <Text variant="small" className="text-muted-foreground">
                      {m.league} · {kickoffDisplay(m.kickoffUtc)}
                    </Text>
                  </View>
                  {m.prediction ? (
                    <Badge variant="secondary">
                      <Text>{m.prediction.expectedTotalGoals.toFixed(1)} xG</Text>
                    </Badge>
                  ) : null}
                </View>

                {m.prediction ? (
                  <Text variant="small" className="text-muted-foreground">
                    BTTS {pct(m.bets.btts.aiPercent)} · U3.5 {pct(m.bets.under35.aiPercent)} · O2.5{' '}
                    {pct(m.bets.over25.aiPercent)}
                  </Text>
                ) : null}

                {placed.length ? (
                  <Text variant="small" className="text-muted-foreground">
                    {placed.length} bet{placed.length === 1 ? '' : 's'}:{' '}
                    {placed.map((b) => `${b.betName} (${b.outcome})`).join(', ')}
                  </Text>
                ) : null}
              </CardContent>
            </Card>
          );
        })
      )}
    </ScrollView>
  );
}
